import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import customerService from '@/services/customer.service';
import type { ReportParams } from '@/services/reportDocuments.service';
import ReportDocumentPanel from './ReportDocumentPanel';
import PeriodRangeFilter from './PeriodRangeFilter';
import { isoDate } from './periodPresets';

const { Title, Text } = Typography;

type Kind = 'receipt' | 'payment';

const TYPES: Record<Kind, string[]> = {
  receipt: ['AgainstInvoice', 'DirectRevenue', 'AdvanceFromCustomer', 'OnAccount', 'Other'],
  payment: ['SupplierBillPayment', 'SupplierAdvance', 'ExpensePayment', 'DirectPayment', 'EmployeePayment', 'BankTransfer', 'CashTransfer', 'AccountTransfer'],
};

const STATUSES = ['Posted', 'Approved', 'Submitted', 'Draft', 'Cancelled'] as const;

const RECEIPT_TYPE_KEYS: Record<string, string> = {
  AgainstInvoice: 'receiptTypeAgainstInvoice',
  DirectRevenue: 'receiptTypeDirectRevenue',
  AdvanceFromCustomer: 'receiptTypeAdvanceFromCustomer',
  OnAccount: 'receiptTypeOnAccount',
  Other: 'receiptTypeOther',
};

const PAYMENT_TYPE_KEYS: Record<string, string> = {
  SupplierBillPayment: 'paymentTypeSupplierBillPayment',
  SupplierAdvance: 'paymentTypeSupplierAdvance',
  ExpensePayment: 'paymentTypeExpensePayment',
  DirectPayment: 'paymentTypeDirectPayment',
  EmployeePayment: 'paymentTypeEmployeePayment',
  BankTransfer: 'paymentTypeBankTransfer',
  CashTransfer: 'paymentTypeCashTransfer',
  AccountTransfer: 'paymentTypeAccountTransfer',
};

const STATUS_KEYS: Record<(typeof STATUSES)[number], string> = {
  Posted: 'statusPosted',
  Approved: 'statusApproved',
  Submitted: 'statusSubmitted',
  Draft: 'statusDraft',
  Cancelled: 'statusCancelled',
};

/** Receipt / payment register on the report-document standard (screen, PDF, Excel, email). */
const VoucherRegisterReport: React.FC<{ kind: Kind }> = ({ kind }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs()]);
  const [status, setStatus] = useState<string>('Posted');
  const [type, setType] = useState<string | undefined>();
  const [partyId, setPartyId] = useState<string | undefined>();
  const [params, setParams] = useState<ReportParams | null>(null);

  const { data: parties = [] } = useQuery({
    queryKey: [kind === 'receipt' ? 'statement-customers' : 'statement-suppliers'],
    queryFn: async () => {
      if (kind === 'receipt') return (await customerService.getActiveCustomers()) as any[];
      const res = await api.get<any>('/procurement/suppliers');
      const data = (res as any)?.data ?? res;
      return (Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []) as any[];
    },
  });
  const partyOptions = useMemo(
    () =>
      parties.map((p: any) => {
        const name = kind === 'receipt' ? p.customer_name : p.supplier_name;
        const code = kind === 'receipt' ? p.customer_code : p.supplier_code;
        return { value: p.id as string, label: `${name ?? ''}${code ? ` (${code})` : ''}` };
      }),
    [parties, kind]
  );

  const typeLabel = (value: string): string => {
    const key = kind === 'receipt' ? RECEIPT_TYPE_KEYS[value] : PAYMENT_TYPE_KEYS[value];
    return key ? t(key) : value;
  };

  const generate = () => {
    const next: ReportParams = { from: isoDate(range[0]), to: isoDate(range[1]), status };
    if (type) next.type = type;
    if (partyId) next.party_id = partyId;
    setParams(next);
  };

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Title level={4}>{kind === 'receipt' ? t('receiptRegister') : t('paymentRegister')}</Title>
      <ReportDocumentPanel
        reportKey={kind === 'receipt' ? 'receipt-register' : 'payment-register'}
        params={params}
        filters={
          <Space wrap size="middle" align="end">
            <PeriodRangeFilter value={range} onChange={setRange} initialPreset="this_month" />
            <Space direction="vertical" size={4}>
              <Text type="secondary">{t('status')}</Text>
              <Select
                value={status}
                onChange={setStatus}
                style={{ width: 140 }}
                options={[
                  ...STATUSES.map((s) => ({ value: s, label: t(STATUS_KEYS[s]) })),
                  { value: 'all', label: t('allStatuses') },
                ]}
              />
            </Space>
            <Space direction="vertical" size={4}>
              <Text type="secondary">{t('type')}</Text>
              <Select
                allowClear
                placeholder={t('allTypes')}
                value={type}
                onChange={setType}
                style={{ width: 200 }}
                options={TYPES[kind].map((typeValue) => ({ value: typeValue, label: typeLabel(typeValue) }))}
              />
            </Space>
            <Space direction="vertical" size={4}>
              <Text type="secondary">{kind === 'receipt' ? t('customer') : t('supplier')}</Text>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('all')}
                value={partyId}
                onChange={setPartyId}
                options={partyOptions}
                style={{ width: 280 }}
              />
            </Space>
            <Button type="primary" icon={<SearchOutlined />} onClick={generate}>
              {tc('generate')}
            </Button>
          </Space>
        }
      />
    </div>
  );
};

export default VoucherRegisterReport;
